// Draw & Guess (Pictionary / Skribbl) Server Engine (Strict Privacy & Custom Settings)
export class DrawGuessEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.wordBank = [
      'PIZZA', 'GUITAR', 'PENGUIN', 'SPIDERMAN', 'CASTLE', 'ASTRONAUT',
      'VOLCANO', 'DRAGON', 'RAINBOW', 'SUBMARINE', 'HELICOPTER', 'PIRATE',
      'BURGER', 'CAMERA', 'DOLPHIN', 'LIGHTSABER', 'EIFFEL TOWER', 'UNICORN',
      'ROBOT', 'FIREWORK', 'SUNGLASSES', 'WATERFALL', 'MOTORCYCLE', 'CAMPFIRE',
      'BICYCLE', 'SKATEBOARD', 'PANDA', 'GIRAFFE', 'OCTOPUS', 'DIAMOND',
      'TREASURE', 'SNOWMAN', 'WIZARD', 'TELESCOPE', 'AIRPLANE', 'SANDCASTLE'
    ];
    this.currentRound = 1;
    this.maxRounds = room.settings?.totalRounds || 3;
    this.roundTimeLimit = room.settings?.roundTimer || 60;
    this.currentDrawerIndex = 0;
    this.currentWord = '';
    this.maskedWord = '';
    this.drawerOptions = [];
    this.roundTimer = this.roundTimeLimit;
    this.timerInterval = null;
    this.drawingStrokes = [];
    this.guessedPlayerIds = new Set();
  }

  startRound() {
    this.drawingStrokes = [];
    this.guessedPlayerIds.clear();
    this.roundTimer = this.roundTimeLimit;

    const drawer = this.room.players[this.currentDrawerIndex % this.room.players.length];
    if (!drawer) return;

    // Pick 3 random words for drawer
    const shuffled = [...this.wordBank].sort(() => 0.5 - Math.random());
    this.drawerOptions = shuffled.slice(0, 3);

    // STRICT PRIVACY: Send word options ONLY to the active drawer socket
    this.io.to(drawer.id).emit('dg_selectWord', { options: this.drawerOptions });

    // Inform other players that the drawer is choosing
    this.io.to(this.room.code).emit('dg_roundStarting', {
      drawerId: drawer.id,
      drawerName: drawer.name,
      round: this.currentRound,
      maxRounds: this.maxRounds
    });

    // If bot is drawer, auto-select a word!
    if (drawer.isBot) {
      setTimeout(() => {
        const choice = this.drawerOptions[0] || 'PIZZA';
        this.chooseWord(drawer.id, choice);
      }, 1400);
    }
  }

  startBotDrawing(drawer) {
    if (this.botDrawInterval) clearInterval(this.botDrawInterval);
    const colors = ['#ffffff', '#ef4444', '#38bdf8', '#22c55e', '#facc15', '#ec4899'];
    const chosenColor = colors[Math.floor(Math.random() * colors.length)];
    let step = 0;
    const cx = 350 + (Math.random() - 0.5) * 80;
    const cy = 200 + (Math.random() - 0.5) * 60;
    const radius = 60 + Math.random() * 40;

    this.botDrawInterval = setInterval(() => {
      step++;
      if (step > 30 || this.roundTimer <= 2) {
        clearInterval(this.botDrawInterval);
        return;
      }
      const a1 = (step / 30) * Math.PI * 2;
      const a2 = ((step + 1) / 30) * Math.PI * 2;
      const stroke = {
        fromX: cx + Math.cos(a1) * radius,
        fromY: cy + Math.sin(a1) * radius,
        toX: cx + Math.cos(a2) * radius,
        toY: cy + Math.sin(a2) * radius,
        color: chosenColor,
        size: 4
      };
      this.handleStroke(drawer.id, stroke);
    }, 400);
  }

  scheduleBotGuesses() {
    const drawer = this.room.players[this.currentDrawerIndex % this.room.players.length];
    const botGuessers = this.room.players.filter(p => p.isBot && p.id !== drawer?.id);
    botGuessers.forEach(b => {
      const delay = 10000 + Math.random() * 16000;
      setTimeout(() => {
        if (this.currentWord && !this.guessedPlayerIds.has(b.id) && this.roundTimer > 3) {
          this.checkGuess(b.id, this.currentWord);
        }
      }, delay);
    });
  }

  chooseWord(drawerId, word) {
    const drawer = this.room.players[this.currentDrawerIndex % this.room.players.length];
    if (!drawer || drawer.id !== drawerId) return;

    this.currentWord = word.toUpperCase();
    this.maskedWord = this.currentWord.replace(/[A-Z]/g, '_ ');

    // Broadcast round started with MASKED dashes ONLY
    this.io.to(this.room.code).emit('dg_roundStarted', {
      drawerId: drawer.id,
      drawerName: drawer.name,
      maskedWord: this.maskedWord,
      letterCount: this.currentWord.length,
      timeLimit: this.roundTimer
    });

    // Send the actual chosen word ONLY to the drawer
    this.io.to(drawer.id).emit('dg_yourWord', { word: this.currentWord });

    if (drawer.isBot) {
      this.startBotDrawing(drawer);
    }
    this.scheduleBotGuesses();

    if (this.timerInterval) clearInterval(this.timerInterval);
    this.timerInterval = setInterval(() => {
      this.roundTimer--;
      this.io.to(this.room.code).emit('dg_tick', { timer: this.roundTimer });

      if (this.roundTimer <= 0 || this.guessedPlayerIds.size >= this.room.players.length - 1) {
        this.endTurn();
      }
    }, 1000);
  }

  handleStroke(drawerId, strokeData) {
    const drawer = this.room.players[this.currentDrawerIndex % this.room.players.length];
    if (!drawer || drawer.id !== drawerId) return;

    this.drawingStrokes.push(strokeData);
    this.io.to(this.room.code).emit('dg_drawStroke', strokeData);
  }

  handleClearCanvas(drawerId) {
    const drawer = this.room.players[this.currentDrawerIndex % this.room.players.length];
    if (!drawer || drawer.id !== drawerId) return;

    this.drawingStrokes = [];
    this.io.to(this.room.code).emit('dg_clearCanvas');
  }

  checkGuess(socketId, guessText) {
    const drawer = this.room.players[this.currentDrawerIndex % this.room.players.length];
    if (!drawer || drawer.id === socketId || !this.currentWord) return { isCorrect: false };
    if (this.guessedPlayerIds.has(socketId)) return { isCorrect: false, alreadyGuessed: true };

    const cleanGuess = guessText.trim().toUpperCase();
    if (cleanGuess === this.currentWord) {
      this.guessedPlayerIds.add(socketId);
      const points = Math.max(50, this.roundTimer * 10);
      const player = this.room.players.find((p) => p.id === socketId);
      if (player) player.score = (player.score || 0) + points;
      drawer.score = (drawer.score || 0) + Math.floor(points / 2);

      this.io.to(this.room.code).emit('dg_correctGuess', {
        playerId: socketId,
        playerName: player?.name || 'Player',
        points,
        scores: this.room.players.map((p) => ({ id: p.id, name: p.name, score: p.score, avatar: p.avatar }))
      });

      if (this.guessedPlayerIds.size >= this.room.players.length - 1) {
        this.endTurn();
      }
      return { isCorrect: true };
    }
    return { isCorrect: false };
  }

  endTurn() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.botDrawInterval) clearInterval(this.botDrawInterval);

    this.io.to(this.room.code).emit('dg_turnEnded', {
      word: this.currentWord,
      scores: this.room.players.map((p) => ({ id: p.id, name: p.name, score: p.score, avatar: p.avatar }))
    });

    this.currentDrawerIndex++;
    if (this.currentDrawerIndex >= this.room.players.length * this.maxRounds) {
      setTimeout(() => {
        const sorted = [...this.room.players].sort((a, b) => b.score - a.score);
        this.io.to(this.room.code).emit('dg_gameOver', { leaderboard: sorted });
      }, 4000);
    } else {
      setTimeout(() => this.startRound(), 4000);
    }
  }

  destroy() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.botDrawInterval) clearInterval(this.botDrawInterval);
  }
}
